"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { sendContactEmail } from "@/lib/email";

const formSchema = z.object({
  name: z.string().min(2, "Nome obrigatório"),
  email: z.string().email("Email inválido"),
  phone: z.string().optional(),
  subject: z.string().min(1, "Assunto obrigatório"),
  message: z.string().min(5, "Mensagem obrigatória"),
  privacy: z.literal(true, {
    errorMap: () => ({
      message: "É necessário aceitar a política de privacidade.",
    }),
  }),
});

type ContactFormValues = z.infer<typeof formSchema>;

const SUBJECT_OPTIONS = [
  "Informações sobre consultas",
  "Produtos em desenvolvimento",
  "Parcerias",
  "Suporte técnico",
  "Outro",
];

export function ContactForm() {
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
      privacy: false as any,
    },
  });

  const onSubmit = async (data: ContactFormValues) => {
    setLoading(true);
    setSuccess(null);
    setError(null);
    try {
      await sendContactEmail(data);
      setSuccess("Mensagem enviada com sucesso!");
      form.reset();
    } catch (e) {
      setError("Ocorreu um erro ao enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nome *</FormLabel>
              <FormControl>
                <Input type="text" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email *</FormLabel>
              <FormControl>
                <Input type="email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Telefone</FormLabel>
              <FormControl>
                <Input type="tel" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="subject"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Assunto *</FormLabel>
              <FormControl>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-blush-500 focus:ring-blush-500"
                  {...field}
                >
                  <option value="">Selecione um assunto</option>
                  {SUBJECT_OPTIONS.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Mensagem *</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Escreva a sua mensagem aqui..."
                  rows={5}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="privacy"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-start">
                <FormControl>
                  <input
                    type="checkbox"
                    id="privacy"
                    checked={field.value}
                    onChange={field.onChange}
                    className="mt-1 mr-3 text-blush-700 focus:ring-blush-500"
                  />
                </FormControl>
                <FormLabel htmlFor="privacy" className="text-sm text-gray-600">
                  Concordo com a{" "}
                  <a href="/termos" className="text-blush-700 contents">
                    Política de Privacidade
                  </a>{" "}
                  e autorizo o tratamento dos meus dados pessoais para resposta
                  a esta mensagem.
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? "A enviar..." : "Enviar Mensagem"}
        </Button>
        {success && <p className="text-green-600 text-center">{success}</p>}
        {error && <p className="text-red-600 text-center">{error}</p>}
      </form>
    </Form>
  );
}
